import React from 'react';
import { Star, Gift, Lock } from 'lucide-react';

const VisualAdventCalendar = ({ calendarDays }) => {
  // Helper function to get current Czech time
  const getCzechTime = () => {
    return new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" });
  };

  // Helper function to check if day should be revealed (16:00 Czech time)
  const shouldDayBeRevealed = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const revealDate = new Date();
    revealDate.setFullYear(currentYear, 11, day); // December (month 11), day
    revealDate.setHours(16, 0, 0, 0); // 16:00:00.000
    
    // Convert reveal date to Czech time for comparison
    const revealDateCzech = new Date(revealDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
    
    return czechTime >= revealDateCzech;
  };

  // Helper function to check if day is currently active (today in Czech time)
  const isDayActive = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const currentMonth = czechTime.getMonth();
    const currentDate = czechTime.getDate();
    
    // Check if today is the specific December day
    return currentMonth === 11 && currentDate === day && currentYear === 2025;
  };

  // Helper function to check if clue should be shown (from 00:00 Czech time)
  const shouldShowClue = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const clueDate = new Date();
    clueDate.setFullYear(currentYear, 11, day); // December (month 11), day
    clueDate.setHours(0, 0, 0, 0); // 00:00:00.000
    
    // Convert clue date to Czech time for comparison
    const clueDateCzech = new Date(clueDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
    
    return czechTime >= clueDateCzech;
  };

  const getBoxStyle = (day) => {
    const isRevealed = shouldDayBeRevealed(day);
    const isActive = isDayActive(day);
    const calendarDay = calendarDays.find(d => d.day === day);
    
    if (isRevealed) {
      // Revealed days - bright green/gold
      return {
        background: 'linear-gradient(135deg, #10B981 0%, #6EE7B7 100%)',
        color: '#FFFFFF',
        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
        border: '2px solid #10B981'
      };
    } else if (isActive) {
      // Active day - golden glow
      return {
        background: 'linear-gradient(135deg, #F59E0B 0%, #FEF3C7 100%)',
        color: '#0A1633',
        boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)',
        border: '2px solid #D4AF37',
        animation: 'pulse 2s infinite'
      };
    } else {
      // Future days - dark/locked
      return {
        background: 'linear-gradient(135deg, #374151 0%, #4B5563 100%)',
        color: '#9CA3AF',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        border: '2px solid #6B7280'
      };
    }
  };

  const getIcon = (day) => {
    const isRevealed = shouldDayBeRevealed(day);
    const isActive = isDayActive(day);
    
    if (isRevealed) {
      return <Star className="w-6 h-6 mb-1" fill="currentColor" />;
    } else if (isActive) {
      return <Gift className="w-6 h-6 mb-1 animate-bounce" />;
    } else {
      return <Lock className="w-4 h-4 mb-1" />;
    }
  };

  const funnyMessages = [
    "🍫 Tenhle kousek čokolády nejde sníst dříve, než můžeš!",
    "🎁 Trpělivost, růže! Ještě není čas na rozbalování...",
    "⏰ Čas plyne pomalu, ale jistě. Počkej si na svůj den!",
    "🔒 Tohle okénko je zatím pod zámkem. Klíč dostaneš až ve správný den!",
    "🕐 Předčasné otevírání je proti pravidlům adventního kalendáře!",
    "🌟 Každá hvězda svítí ve svůj čas. Tahla ještě spí...",
    "🎄 Vánoční strom se také nerozsvěcuje najednou!",
    "🍪 Než upečeš vánoční cukroví, musíš počkat na správné datum!",
    "❄️ Jako sníh padá ve svůj čas, tak se i toto okénko otevře ve svůj den!",
    "🎊 Předčasné oslavy jsou jako nedozrálé ovoce - chutná to líp, když si počkáš!",
    "🔮 Kouzelná věta dne: 'Počkej si, až bude čas!'",
    "✨ Jako se hvězdička Třpytka rozsvěcuje postupně, tak i ty si počkej na svou chvíli!",
  ];

  const showToast = (message) => {
    // Create toast element
    const toast = document.createElement('div');
    toast.innerHTML = message;
    toast.className = 'fixed top-4 right-4 bg-gold text-dark-blue px-6 py-3 rounded-lg shadow-lg font-semibold z-50 animate-bounce';
    toast.style.animation = 'slideIn 0.3s ease-out, fadeOut 0.5s ease-in 2.5s';
    
    document.body.appendChild(toast);
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  const handleBoxClick = (day) => {
    const calendarDay = calendarDays.find(d => d.day === day);
    const isRevealed = shouldDayBeRevealed(day);
    const isActive = isDayActive(day);
    
    if (isRevealed || isActive) {
      // Scroll to map section to show location
      document.getElementById('calendar-map')?.scrollIntoView({ 
        behavior: 'smooth' 
      });
    } else {
      // Show funny message for locked days
      const randomMessage = funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
      showToast(randomMessage);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-4xl font-serif font-bold text-gold text-center mb-8">
        Adventní kalendář
      </h2>
      <p className="text-center text-white/80 mb-12 text-lg">
        Každý den se rozsvítí nové okénko. Klikněte na rozsvícené dny pro více informací.
      </p>
      
      <div className="grid grid-cols-4 md:grid-cols-6 gap-3 md:gap-4 mb-8">
        {Array.from({ length: 24 }, (_, index) => {
          const day = index + 1;
          
          return (
            <div
              key={day}
              className="relative bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl h-20 md:h-24 flex flex-col items-center justify-center transition-all duration-300 hover:scale-105 cursor-pointer border-2 border-gray-500"
              style={getBoxStyle(day)}
              onClick={() => handleBoxClick(day)}
            >
              {/* Content */}
              <div className="text-center">
                {getIcon(day)}
                <div className="font-serif font-bold text-lg md:text-xl">
                  {day}
                </div>
                {shouldDayBeRevealed(day) && (
                  <div className="text-xs mt-1 opacity-90">
                    Rozsvíceno
                  </div>
                )}
                {isDayActive(day) && (
                  <div className="text-xs mt-1 font-semibold">
                    Dnes!
                  </div>
                )}
              </div>
              
              {/* Special day indicators */}
              {day === 24 && (
                <div className="absolute top-1 right-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                </div>
              )}
              {isDayActive(day) && (
                <div className="absolute inset-0 rounded-xl animate-pulse bg-gradient-to-br from-transparent to-yellow-400/20"></div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center items-center gap-6 flex-wrap text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gradient-to-r from-green-500 to-green-300 border border-green-400"></div>
          <span className="text-white/80">Rozsvíceno</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gradient-to-r from-yellow-500 to-yellow-300 border border-yellow-400 animate-pulse"></div>
          <span className="text-white/80">Dnes se rozsvítí</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gradient-to-r from-gray-500 to-gray-400 border border-gray-500"></div>
          <span className="text-white/80">Čeká na rozsvícení</span>
        </div>
      </div>
    </div>
  );
};

export default VisualAdventCalendar;